<script setup lang="ts">
import type { FeatureCard } from '@/types/landing'
import {
  Target,
  FileText,
  Brain,
  BookOpen,
  Megaphone,
  Archive,
} from 'lucide-vue-next'

interface Props {
  features: FeatureCard[]
}

const props = defineProps<Props>()

const iconMap: Record<string, any> = {
  Target,
  FileText,
  Brain,
  BookOpen,
  Megaphone,
  Archive,
}
</script>

<template>
  <section id="features" class="bg-white py-20 md:py-24">
    <div class="mx-auto max-w-[1200px] px-6">
      <!-- 标题区 -->
      <div class="mb-14 text-center">
        <h2 class="mb-3 text-2xl font-semibold text-[#1D2129] md:text-3xl">
          从任务跟踪到自动归档，全过程一站搞定
        </h2>
        <p class="mx-auto max-w-[560px] text-base text-[#86909C]">
          每个环节都有智能辅助，让基层工作更轻松
        </p>
      </div>

      <!-- 功能卡片网格 -->
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <article
          v-for="feature in props.features"
          :key="feature.id"
          class="group relative rounded-2xl border border-[#E5E6EB] bg-white p-6 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
        >
          <!-- 图标 -->
          <div
            class="mb-5 flex h-14 w-14 items-center justify-center rounded-full"
            :style="{ background: feature.color + '15' }"
          >
            <component
              :is="iconMap[feature.icon as keyof typeof iconMap]"
              class="h-7 w-7"
              :style="{ color: feature.color }"
            />
          </div>

          <!-- 标题 -->
          <h3 class="mb-1 text-lg font-semibold text-[#1D2129]">
            {{ feature.title }}
          </h3>

          <!-- 副标题 -->
          <p class="mb-4 text-sm text-[#86909C]">
            {{ feature.subtitle }}
          </p>

          <!-- 功能亮点 -->
          <ul class="mb-5 space-y-2">
            <li
              v-for="(highlight, index) in feature.highlights"
              :key="index"
              class="flex items-start gap-2 text-sm text-[#4E5969]"
            >
              <span
                class="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full"
                :style="{ backgroundColor: feature.color }"
              />
              {{ highlight }}
            </li>
          </ul>

          <!-- 了解更多 -->
          <a
            href="#"
            class="inline-flex items-center gap-1 text-sm font-medium transition-colors hover:opacity-80"
            :style="{ color: feature.color }"
            :aria-label="`了解更多关于${feature.title}功能`"
          >
            了解更多
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M6 3l5 5-5 5" />
            </svg>
          </a>
        </article>
      </div>
    </div>
  </section>
</template>
